import { useCallback, useEffect, useState } from "react";

/**
 * Hook per gestire il consenso cookie conforme al provvedimento del Garante
 * privacy del 10 giugno 2021 e al GDPR (artt. 6, 7).
 *
 * - Persiste la scelta in localStorage per 6 mesi.
 * - `necessary` è sempre `true` (base giuridica: legittimo interesse).
 * - Espone un event bus custom (`cookieconsent:updated`, `cookie:open`)
 *   così script di terze parti possono caricarsi in modo condizionale.
 */

const STORAGE_KEY = "dasabo_cookie_consent_v1";
const VERSION = 1;
const TTL_MS = 1000 * 60 * 60 * 24 * 180; // 6 mesi

export const DEFAULT_CATEGORIES = Object.freeze({
  necessary: true,
  preferences: false,
  statistics: false,
  marketing: false,
});

export function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.version !== VERSION) return null;
    if (Date.now() > parsed.expiresAt) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function writeConsent(categories) {
  const merged = { ...DEFAULT_CATEGORIES, ...categories, necessary: true };
  const payload = {
    version: VERSION,
    acceptedAt: Date.now(),
    expiresAt: Date.now() + TTL_MS,
    categories: merged,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  window.dispatchEvent(
    new CustomEvent("cookieconsent:updated", { detail: payload })
  );
  return payload;
}

export function clearConsent() {
  localStorage.removeItem(STORAGE_KEY);
  window.dispatchEvent(new CustomEvent("cookieconsent:cleared"));
}

/**
 * Riapre il banner in modalità "Personalizza" — utile per il link
 * `Preferenze cookie` presente nei footer.
 */
export function openCookiePreferences() {
  window.dispatchEvent(new CustomEvent("cookie:open"));
}

export default function useCookieConsent() {
  const [consent, setConsent] = useState(() => readConsent());

  useEffect(() => {
    const onUpdate = (e) => setConsent(e.detail || readConsent());
    const onClear = () => setConsent(null);
    window.addEventListener("cookieconsent:updated", onUpdate);
    window.addEventListener("cookieconsent:cleared", onClear);
    return () => {
      window.removeEventListener("cookieconsent:updated", onUpdate);
      window.removeEventListener("cookieconsent:cleared", onClear);
    };
  }, []);

  const acceptAll = useCallback(() => {
    return writeConsent({
      preferences: true,
      statistics: true,
      marketing: true,
    });
  }, []);

  const rejectAll = useCallback(() => {
    return writeConsent({
      preferences: false,
      statistics: false,
      marketing: false,
    });
  }, []);

  const savePartial = useCallback((categories) => {
    return writeConsent(categories);
  }, []);

  return {
    consent,
    hasConsented: Boolean(consent),
    categories: consent?.categories || DEFAULT_CATEGORIES,
    acceptAll,
    rejectAll,
    savePartial,
    reopen: openCookiePreferences,
  };
}
